import React, { useState, useEffect } from 'react';
import OnceFooter from "./../common/Footer/OnceFooter";
import { useParams, Link } from 'react-router-dom';
import api from './../../utils/api';
import { Modal, Button, Row, Col, Collapse, Card, Nav, Tab } from 'react-bootstrap';
import { numberFormat, transformFieldWithLang } from './../../utils/functions';
import moment from "moment";
import 'moment/locale/th'
import _ from "lodash";
import { useTranslation } from 'react-i18next'
import ChangeLanguageDropdown from '../common/ChangeLanguageDropdown';

export default function BookingCartStatus() {
  const { id, code } = useParams();
  const [cart, setCart] = useState({});
  const { hotel, bookings } = cart;
  const [showCollapse, setShowCollapse] = useState([]);

  const booking = bookings && bookings.length > 0 ? bookings[0] : null;

  const endDate = new Date(booking?.end_date)
  const endDateStr = endDate.setDate(endDate.getDate() + 1)

  const [policies, setPolicies] = useState([]);
  const [foods, setFoods] = useState([]);
  const [attractions, setAttractions] = useState([]);

  const { t, i18n } = useTranslation()
  const lang = i18n.language || 'th'

  const fetchPolicies = (_hotel_id, _room_ids = []) => {
    if (!_hotel_id) return;

    let params = {
      hotel_id: _hotel_id,
      enabled: true,
      types: ['hotel_policy']
    }

    if (_room_ids && _room_ids.length > 0) {
      params.types.push('room_policy')
      params.with_room_ids = _room_ids
    }

    api.request(`hotels/hotel-info/all`, (data) => {
      if (!data || data.length === 0) {
        setPolicies([]);
        return;
      }

      setPolicies(data);
    }, params)
  }

  const fetchRecommentPlaces = (_hotel_id) => {
    if (!_hotel_id) return;

    api.request(`hotels/recommend-nearby-places/all`, (data) => {
      if (!data || data.length === 0) {
        setFoods([]);
        setAttractions([]);
        return;
      }

      const foodsData = data.filter((d) => d.type === 'food') || [];
      const attractionsData = data.filter((d) => d.type === 'attraction') || [];

      setFoods(foodsData);
      setAttractions(attractionsData);
    }, {
      hotel_id: _hotel_id,
      enabled: true,
    })
  }

  const fetchBookingData = () => {
    api.request(`bookings/findonebookingcart`, (data) => {
      setCart(data || {})
      setShowCollapse(Array(data?.bookings?.length).fill(true));

      if (data?.hotel?.show_hotel_info_to_customer) {
        fetchRecommentPlaces(data?.hotel_id);
        fetchPolicies(data?.hotel_id, (data?.bookings || [])?.map((b) => b.room_id) || []);
      }
    }, {
      id: id,
      transaction_no: code,
    })
  }

  useEffect(() => {
    if (!!id) {
      fetchBookingData()
    }
  }, [id])

  const warningMsg = booking?.payment_state === 'complete' ? t('booking_notify_info_complete') : t('booking_notify_info_wait_payment')

  const hotelPolicy = policies.find((p) => p.type === 'hotel_policy') || null;
  const roomPolicies = policies.filter((p) => p.type === 'room_policy') || [];

  const [placeHtmlDataForShow, setPlaceHtmlDataForShow] = useState(null);
  const _doc = cart?.bookings && cart?.bookings.length > 0 ? cart?.bookings[0]?.receipt_file_path : null;

  const totalOutStanding = bookings ? bookings.reduce((sum, b) => {
    return sum + (Number(b.net_total || 0) - Number(b.paid_amount || 0))
  }, 0) : 0;

  const bookingOverviewSection = () => {
    return (
      <Row>
        {!!_doc && <Col xs={12} className="mb-3">
          <Button variant="success" size="sm" onClick={() => {
            window.open(_doc, '_blank')
          }}>{t('download')}{t('booking_summary_doc')}</Button>
        </Col>}
        <Col xs={12} md={6}>
          <Row>
            <Col xs={12}>
              <span className="title">{t('transaction_no')} : </span>
              <span className="detail">{cart.transaction_no}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('stay_date')} : </span>
              <span className="detail">{moment(booking?.start_date).format('DD/MM/YYYY')} - {moment(endDateStr).format('DD/MM/YYYY')} ({booking?.day_qty} {t('nights')})</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('contact_name')}  : </span>
              <span className="detail">{booking.contact_name}</span>
            </Col>
            {!!hotel && hotel.request_customer_tax_info_enabled && <Col xs={12}>
              <span className="title">{t('customer_tax_id')} : </span>
              <span className="detail">{booking.customer_tax_id}</span>
            </Col>}
            {!!hotel && hotel.request_customer_tax_info_enabled && <Col xs={12}>
              <span className="title">{t('tax_invoice_address')} : </span>
              <span className="detail">{booking.customer_address || '-'}</span>
            </Col>}
            <Col xs={12}>
              <span className="title">{t('telephone')} : </span>
              <span className="detail">{booking.contact_tel_1} {!!booking.contact_tel_2 ? `/ ${booking.contact_tel_2}` : ''}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('email')} : </span>
              <span className="detail">{booking.contact_email || '-'}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('line_id')} : </span>
              <span className="detail">{booking.contact_line_id || '-'}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('car_model')} : </span>
              <span className="detail">{booking.customer_car_model || '-'}</span>
            </Col>
            <Col xs={12}>
              <span className="title">{t('car_license_plate')} : </span>
              <span className="detail">{booking.customer_car_license_plate || '-'}</span>
            </Col>
          </Row>
        </Col>
        <Col xs={12} md={6}>
          {bookings && bookings.length > 0 && bookings.map((b, bIndex) => {
            const addons = b.booking_addons || [];
            const addonsText = (b.booking_addons || []).map((x) => { return `${transformFieldWithLang(x?.addons_type, 'name', lang)} (${x?.qty})` })
            const addonsTextStr = (addonsText || []).join(', ')
            const addonsSum = _.sumBy(b.booking_addons || [], (x) => { return Number(x.total_price || 0) }) || 0

            const outstanding = (Number(b.net_total || 0) - Number(b.paid_amount || 0))
            const roomDeposit = !!b.room_deposits && b.room_deposits.length > 0 ? b.room_deposits[0] : null

            const additionalPrice = Number(b.additional_guest_total_price || 0)
            const addtiionalPet = b.booking_additional_pets && b.booking_additional_pets.length > 0 ? b.booking_additional_pets[0] : null;

            return (
              <Card key={bIndex} className="mb-3">
                <Card.Header aria-controls={`collapse_booking_${bIndex}`} aria-expanded={showCollapse[bIndex]} onClick={() => {
                  let newShowCollapse = [...showCollapse];
                  newShowCollapse[bIndex] = !newShowCollapse[bIndex];
                  setShowCollapse(newShowCollapse);
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{b.booking_no} : {b.room?.name_th}</h5>
                    <span className="toggle-collapse"><i className={`fas fa-chevron-${showCollapse[bIndex] ? 'up' : 'down'}`}></i> {showCollapse[bIndex] ? t('hide_details') : t('show_details')}</span>
                  </div>
                </Card.Header>
                <Collapse in={showCollapse[bIndex]}>
                  <Card.Body id={`collapse_booking_${bIndex}`}>
                    <Row>
                      <Col xs={12}>
                        <span className="title">{t('booking_no')} : </span>
                        <span className="detail">{b.booking_no}</span>
                      </Col>
                      <Col xs={12}>
                        <span className="title">{t('room')} : </span>
                        <span className="detail">{transformFieldWithLang(b.room, 'name', lang)}</span>
                      </Col>
                      <Col xs={12}>
                        <span className="title">{t('guest')} : </span>
                        <span className="detail">{t('adult')} {numberFormat(b.guest_qty, true)} {t('persons')}, {t('baby', { age: hotel?.custom_guest_baby_age || 6 })} {numberFormat(b.guest_baby_qty, true)} {t('persons')}</span>
                      </Col>
                      {additionalPrice > 0 && <Col xs={12}>
                        <span className="title">{t('guest')} ({t('additional')}) : </span>
                        <span className="detail text-danger">{t('adult')} {numberFormat(b.additional_guest_qty, true)} {t('persons')}, {t('baby', { age: hotel?.custom_guest_baby_age || 6 })} {numberFormat(b.additional_baby_qty, true)} {t('persons')}</span>
                      </Col>}
                      {addtiionalPet && <Col xs={12}>
                        <span className="title">{t('pet')} ({t('additional')}) : </span>
                        <span className="detail text-danger">{transformFieldWithLang(addtiionalPet.pet_type, 'name', lang)} {t('qty')} {numberFormat(addtiionalPet.qty, true)} {t('pet_unit')}</span>
                      </Col>}
                      <Col xs={12}>
                        <span className="title">{t('payment_methods')} : </span>
                        {b.payment_type === 'payonstay' && <span className="badge bg-danger">{t('payment_method_pay_on_stay')}</span>}
                        {b.payment_type === 'deposit' && <span className="badge bg-danger">{t('payment_method_deposit')}</span>}
                        {b.payment_type === 'full' && <span className="badge bg-success">{t('payment_method_full')}</span>}
                        {(!!roomDeposit || b.room_deposit_outstanding > 0) && <span className="badge bg-secondary ms-1">{t('has_security_deposit')}</span>}
                      </Col>
                      {!!roomDeposit && <Col xs={12}>
                        <span className="title">{t('security_deposit')} : </span>
                        <span className="detail text-success">{numberFormat(roomDeposit.deposit_amount)} {t('currency_unit')} ({t('paid')})</span>
                      </Col>}
                      {b.room_deposit_outstanding > 0 && <Col xs={12}>
                        <span className="title">{t('security_deposit')} : </span>
                        <span className="detail text-danger">{numberFormat(b.room_deposit_outstanding)} {t('currency_unit')} ({t('please_pay_on_stay')})</span>
                      </Col>}
                      <Col xs={12}>
                        <span className="title">{t('room_price')} : </span>
                        <span className="detail">{numberFormat(b.total_price)} {t('currency_unit')}</span>
                      </Col>
                      {additionalPrice > 0 && <Col xs={12}>
                        <span className="title">{t('guest_additional_price')} : </span>
                        <span className="detail">{numberFormat(additionalPrice)} {t('currency_unit')}</span>
                      </Col>}
                      {addtiionalPet && <Col xs={12}>
                        <span className="title">{t('pet_additional_price')} : </span>
                        <span className="detail">{numberFormat(addtiionalPet.total_price)} {t('currency_unit')}</span>
                      </Col>}
                      {addonsSum > 0 && <Col xs={12}>
                        <span className="title">{t('addons_price')} : </span>
                        <span className="detail">{numberFormat(addonsSum)} {t('currency_unit')}</span>
                      </Col>}
                      <Col xs={12}>
                        <span className="title">{t('discount')} : </span>
                        <span className="detail">{numberFormat(b.total_discount)} {t('currency_unit')}</span>
                      </Col>

                      <Col xs={12}>
                        <span className="title">{t('room_charge_paid')} : </span>
                        <span className="detail text-success">{numberFormat(b.paid_amount)} {t('currency_unit')}</span>
                      </Col>
                      <Col xs={12}>
                        <span className="title">{t('room_charge_outstanding')} : </span>
                        <span className={`detail ${outstanding > 0 ? 'text-danger' : ''}`}>{numberFormat(outstanding)} {t('currency_unit')} {outstanding > 0 && <small className="text-warning"> *{t('please_settle_outstanding_on_stay')}</small>}</span>
                      </Col>
                      {!!roomDeposit && <Col xs={12}>
                        <span className="title">{t('net_payment_amount')} : </span>
                        <span className="badge bg-success">{numberFormat(Number(b.paid_amount || 0) + Number(roomDeposit.deposit_amount || 0))} {t('currency_unit')}</span>
                      </Col>}
                      {booking.room_deposit_outstanding > 0 && <Col xs={12}>
                        <span className="title">{t('pay_amount_on_stay')}  : </span>
                        <span className="badge bg-danger">{numberFormat(Number(outstanding || 0) + Number(b.room_deposit_outstanding || 0))} {t('currency_unit')}</span>
                      </Col>}
                      <Col xs={12}>
                        <span className="title">{t('payment_status')} : </span>
                        {b.payment_state === 'waiting' && <span className="badge bg-warning">{t('payment_status_waiting_approve')}</span>}
                        {b.payment_state === 'waiting-approved' && <span className="badge bg-danger">{t('payment_status_pay_on_stay')}</span>}
                        {b.payment_state === 'complete' && <span className="badge bg-success">{t('payment_status_complete')}</span>}
                        {b.payment_state === 'partial' && <span className="badge bg-info">{t('payment_status_deposit_complete')}</span>}
                      </Col>
                      <Col xs={12}>
                        <div>
                          <span className="title">{t('note')} : </span>
                          <span className="detail">{b.booking_note || '-'}</span>
                        </div>
                        {(addons && addons.length > 0) && <div>
                          <span className="title">{t('addons')} : </span>
                          <span className="detail">{addonsTextStr || '-'}  {t('total_price')} {numberFormat(addonsSum)} {t('currency_unit')}</span>
                        </div>}
                        {b?.freeAddon && <div>
                          <span className="title">{t('free_addons')} : </span>
                          <span className="detail">{transformFieldWithLang(b.freeAddon, 'name', lang) || '-'}</span>
                        </div>}
                      </Col>
                    </Row>
                  </Card.Body>
                </Collapse>
              </Card>
            )
          })}
        </Col>
      </Row>
    )
  };

  const bookingOverviewTabs = () => {
    return (
      <Tab.Container defaultActiveKey="overview">
        <Nav variant="tabs" defaultActiveKey="overview" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="overview">{t('booking_detail_title')}</Nav.Link>
          </Nav.Item>
          {!!totalOutStanding > 0 && <Nav.Item>
            <Nav.Link eventKey="payment">{t('payment_methods')}</Nav.Link>
          </Nav.Item>}
          {!!hotelPolicy && <Nav.Item>
            <Nav.Link eventKey="terms">{transformFieldWithLang(hotelPolicy, 'title', lang) || t('hotel_policy')}</Nav.Link>
          </Nav.Item>}
          {roomPolicies.map((rp, rpIndex) => (
            <Nav.Item key={`room_policy_nav_${rpIndex}`}>
              <Nav.Link eventKey={`room_terms_${rpIndex}`}>{transformFieldWithLang(rp, 'title', lang) || t('room_policy')}</Nav.Link>
            </Nav.Item>
          ))}
          {foods.length > 0 && <Nav.Item>
            <Nav.Link eventKey="foods">{t('food_suggestion')}</Nav.Link>
          </Nav.Item>}
          {attractions.length > 0 && <Nav.Item>
            <Nav.Link eventKey="attractions">{t('attractions_suggestion')}</Nav.Link>
          </Nav.Item>}
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="overview">
            {bookingOverviewSection()}
          </Tab.Pane>
          {!!totalOutStanding > 0 && <Tab.Pane eventKey="payment" className="booking-policy">
            <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: transformFieldWithLang(hotel, 'payment_info', lang) || `<p>${t('please_contact_admin_for_request_payment_methods')}</p>` }} />
          </Tab.Pane>}
          {!!hotelPolicy && <Tab.Pane eventKey="terms" className="booking-policy">
            <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: transformFieldWithLang(hotelPolicy, 'detail', lang) }} />
          </Tab.Pane>}
          {roomPolicies.map((rp, rpIndex) => (
            <Tab.Pane key={`room_policy_pane_${rpIndex}`} eventKey={`room_terms_${rpIndex}`} className="booking-policy">
              <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: transformFieldWithLang(rp, 'detail', lang) }} />
            </Tab.Pane>
          ))}
          {foods.length > 0 && <Tab.Pane eventKey="foods">
            <Row>
              {foods.map((item, _i) => (
                <Col key={item.id} xs={12} lg={6} xl={4}>
                  <div className="d-block d-sm-flex align-items-center mt-2">
                    {!!item.image_url && <img className="rounded" style={{ width: 150 }} src={item.image_url} alt="" />}
                    {!item.image_url && <div className="text-center p-2 border rounded">
                      <i className="fal fa-images text-muted" style={{ fontSize: 20 }}></i>
                      <div><small className=" text-muted">{t('no_image')}</small></div>
                    </div>}
                    <div className="ms-0 ms-sm-3 mt-2 mb-3 my-md-0">
                      <h5 className="m-0">{transformFieldWithLang(item, 'name', lang)}</h5>
                      {!!transformFieldWithLang(item, 'description', lang) && <div className="mb-0">{transformFieldWithLang(item, 'description', lang)}</div>}
                      {!!item.tel && <div className="mt-1 text-black">
                        <a href={`tel:${item.tel}`}>
                          <i className="fas fa-phone-alt"></i> {item.tel}
                        </a>
                      </div>}
                      {!!item.line_url && <div className="mt-1 text-success">
                        <a href={item.line_url} target="_blank" rel="noreferrer">
                          <i className="fab fa-line"></i> {t('contact_via_line')}
                        </a>
                      </div>}
                      {!!item.google_map_url && <div className="mt-1 text-primary">
                        <a href={item.google_map_url} target="_blank" rel="noreferrer">
                          <i className="fas fa-map-marker-alt"></i> {t('view_map')}
                        </a>
                      </div>}
                      {!!item.place_link && <div className="mt-1 text-secondary">
                        <a href={item.place_link} target="_blank" rel="noreferrer">
                          <i className="fas fa-globe"></i> {t('website')}
                        </a>
                      </div>}
                      {!!transformFieldWithLang(item, 'detail', lang) && <div className="mt-1 text-secondary">
                        <Button variant="secondary" className="btn-sm mt-0" onClick={() => {
                          setPlaceHtmlDataForShow(transformFieldWithLang(item, 'detail', lang))
                        }}>{t('view_more')}</Button>
                      </div>}
                      <hr className="d-block d-sm-none" style={{ color: 'darkgray' }} />
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Tab.Pane>}
          {attractions.length > 0 && <Tab.Pane eventKey="attractions">
            <Row>
              {attractions.map((item, _i) => (
                <Col key={item.id} xs={12} lg={6} xl={4}>
                  <div className="d-block d-sm-flex align-items-center mt-2">
                    {!!item.image_url && <img className="rounded" style={{ width: 80 }} src={item.image_url} alt="" />}
                    {!item.image_url && <div className="text-center p-2 border rounded">
                      <i className="fal fa-images text-muted" style={{ fontSize: 20 }}></i>
                      <div><small className=" text-muted">{t('no_image')}</small></div>
                    </div>}
                    <div className="ms-0 ms-sm-3 mt-2 mb-3 my-md-0">
                      <h5 className="m-0">{transformFieldWithLang(item, 'name', lang)}</h5>
                      {!!transformFieldWithLang(item, 'description', lang) && <div className="mb-0">{transformFieldWithLang(item, 'description', lang)}</div>}
                      {!!item.tel && <div className="mt-1 text-black">
                        <a href={`tel:${item.tel}`}>
                          <i className="fas fa-phone-alt"></i> {item.tel}
                        </a>
                      </div>}
                      {!!item.google_map_url && <div className="mt-1 text-success">
                        <a href={item.google_map_url} target="_blank" rel="noreferrer">
                          <i className="fas fa-map-marker-alt"></i> {t('view_map')}
                        </a>
                      </div>}
                      {!!item.place_link && <div className="mt-1 text-secondary">
                        <a href={item.place_link} target="_blank" rel="noreferrer">
                          <i className="fas fa-info-circle"></i> {t('view_more')}
                        </a>
                      </div>}
                      <hr className="d-block d-sm-none" style={{ color: 'darkgray' }} />
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Tab.Pane>}
        </Tab.Content>
      </Tab.Container>
    )
  }

  return (
    <>
      <div className="text-center">
        {!cart?.id && <div className="p-5">
          <h4>{t('booking_not_found')}</h4>
          <div>{t('please_check_link_correct_or_contact_admin')}</div>
        </div>}

        {!!cart?.id && <div className="p-5 pt-2 booking-status">
          <div className="text-end py-2">
            <ChangeLanguageDropdown />
          </div>
          <p className="text-success text-start text-md-center">{warningMsg}</p>
          <h4>{t('booking_detail')}</h4>
          <div className="text-start px-2 px-md-5 py-3">
            {/* โลโก้กับชื่อที่พักกดกลับหน้าแรกได้ -- เป็นที่ที่คนคุ้นว่ากดแล้วกลับบ้าน
              * แต่ไม่พอเพราะไม่มีอะไรบอกว่ากดได้ จึงมีปุ่มชัดๆ ท้ายหน้าด้วย */}
            <Link
              to={`/${hotel?.code || ''}`}
              className="d-flex align-items-center text-reset text-decoration-none"
              title={t('back_to_hotel_home')}
            >
              {!!hotel?.logo_file_path && <img src={hotel?.logo_file_path} className="hotel-logo" alt="img" />}
              <h5 className="mx-2 mb-0">{transformFieldWithLang(hotel, 'name', lang)}</h5>
            </Link>
            {hotel?.show_hotel_info_to_customer && <div>
              {bookingOverviewTabs()}
            </div>}
            {!hotel?.show_hotel_info_to_customer && <div>
              {bookingOverviewSection()}
            </div>}
          </div>

          {/* อ่านจบแล้วต้องมีทางไปต่อ -- ก่อนหน้านี้หน้านี้เป็นทางตัน
            * ลูกค้าที่อยากจองเพิ่มหรือดูห้องอื่นต้องกดย้อนกลับหรือพิมพ์ลิงก์เอง */}
          {!!hotel?.code && (
            <div className="text-center pt-2">
              <Link to={`/${hotel.code}`} className="btn btn-outline-dark px-4">
                {t('back_to_hotel_home')}
              </Link>
            </div>
          )}
        </div>}
      </div >
      <Modal size="lg" show={!!placeHtmlDataForShow} onHide={() => setPlaceHtmlDataForShow(null)} centered>
        <Modal.Body className="booking-policy">
          <div className="booking-term-condition" dangerouslySetInnerHTML={{ __html: placeHtmlDataForShow }} />
        </Modal.Body>
      </Modal>
      <OnceFooter />
    </>
  );
}
